import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  SparklesIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  LinkIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { useAppStore } from '../store/useAppStore'
import { generateMarketingContent } from '../lib/groq'
import { clsx } from 'clsx'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

type MarketingTab = 'email' | 'whatsapp' | 'telegram' | 'social'

const tabs: { id: MarketingTab; name: string; icon: any }[] = [
  { id: 'email', name: 'Email', icon: EnvelopeIcon },
  { id: 'whatsapp', name: 'WhatsApp', icon: ChatBubbleLeftIcon },
  { id: 'telegram', name: 'Telegram', icon: PaperAirplaneIcon },
  { id: 'social', name: 'Social Media', icon: DevicePhoneMobileIcon },
]

type SmtpProvider = 'Amazon SES' | 'SendGrid' | 'Mailgun' | 'Custom SMTP'

const smtpProviders: SmtpProvider[] = ['Amazon SES', 'SendGrid', 'Mailgun', 'Custom SMTP']

interface EmailCampaign {
  id: number
  name: string
  status: 'active' | 'draft' | 'paused' | 'completed'
  sent: number
  opened: number
  clicked: number
  bounced: number
  scheduledFor: string | null
}

const initialCampaigns: EmailCampaign[] = [
  { id: 1, name: 'Welcome Series', status: 'active', sent: 1243, opened: 892, clicked: 341, bounced: 12, scheduledFor: null },
  { id: 2, name: 'Product Launch', status: 'draft', sent: 0, opened: 0, clicked: 0, bounced: 0, scheduledFor: '2026-08-01T09:00' },
  { id: 3, name: 'Re-engagement', status: 'active', sent: 567, opened: 312, clicked: 98, bounced: 23, scheduledFor: null },
  { id: 4, name: 'Newsletter - July', status: 'paused', sent: 3421, opened: 2156, clicked: 876, bounced: 45, scheduledFor: null },
  { id: 5, name: 'Webinar Invite', status: 'completed', sent: 2100, opened: 1430, clicked: 621, bounced: 18, scheduledFor: null },
]

interface WhatsAppContact {
  id: number
  name: string
  phone: string
  status: 'active' | 'inactive'
  lastMessage: string
  messageStatus: 'delivered' | 'read' | 'pending' | 'failed'
}

const initialWhatsappContacts: WhatsAppContact[] = [
  { id: 1, name: 'John Smith', phone: '+1 234 567 890', status: 'active', lastMessage: 'Thanks for the update!', messageStatus: 'read' },
  { id: 2, name: 'Sarah Johnson', phone: '+1 234 567 891', status: 'active', lastMessage: 'When is the meeting?', messageStatus: 'delivered' },
  { id: 3, name: 'Mike Brown', phone: '+1 234 567 892', status: 'inactive', lastMessage: 'Got it, thanks!', messageStatus: 'read' },
  { id: 4, name: 'Emily Davis', phone: '+1 234 567 893', status: 'active', lastMessage: 'Interested in the demo', messageStatus: 'pending' },
  { id: 5, name: 'Alex Turner', phone: '+1 234 567 894', status: 'active', lastMessage: 'Please resend the link', messageStatus: 'failed' },
]

interface SocialPlatform {
  id: number
  name: string
  icon: string
  followers: number
  engagement: number
  status: 'connected' | 'disconnected' | 'pending'
  postsScheduled: number
}

const initialSocialPlatforms: SocialPlatform[] = [
  { id: 1, name: 'Facebook', icon: '📘', followers: 12500, engagement: 4.2, status: 'connected', postsScheduled: 3 },
  { id: 2, name: 'Instagram', icon: '📸', followers: 8900, engagement: 5.8, status: 'connected', postsScheduled: 5 },
  { id: 3, name: 'Twitter / X', icon: '🐦', followers: 5600, engagement: 2.1, status: 'connected', postsScheduled: 2 },
  { id: 4, name: 'LinkedIn', icon: '💼', followers: 3200, engagement: 3.4, status: 'disconnected', postsScheduled: 0 },
  { id: 5, name: 'TikTok', icon: '🎵', followers: 15000, engagement: 8.2, status: 'pending', postsScheduled: 1 },
]

const messageTemplates = [
  { id: 1, name: 'Welcome Greeting', body: 'Hello {{name}}! Welcome to our community. How can we help you today?' },
  { id: 2, name: 'Order Confirmation', body: 'Hi {{name}}, your order #{{orderId}} has been confirmed. Expected delivery: {{date}}.' },
  { id: 3, name: 'Follow-up', body: 'Hi {{name}}, just checking in to see if you had a chance to review our proposal.' },
  { id: 4, name: 'Promotion', body: '🎉 Special offer for you, {{name}}! Get {{discount}}% off your next purchase. Use code: {{code}}' },
]

export const Marketing = () => {
  const [activeTab, setActiveTab] = useState<MarketingTab>('email')
  const [searchQuery, setSearchQuery] = useState('')

  // Toast / feedback state
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false })
  const showToast = useCallback((message: string) => {
    setToast({ message, visible: true })
    setTimeout(() => setToast({ message: '', visible: false }), 3000)
  }, [])

  // ─── Email state ──────────────────────────────────────────
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>(initialCampaigns)
  const [isCreateCampaignOpen, setIsCreateCampaignOpen] = useState(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)
  const [newCampaign, setNewCampaign] = useState({ name: '', subject: '', body: '' })
  const [scheduleDate, setScheduleDate] = useState('')
  const [scheduleTime, setScheduleTime] = useState('')

  // SMTP state
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('587')
  const [smtpUsername, setSmtpUsername] = useState('')
  const [smtpPassword, setSmtpPassword] = useState('')
  const [smtpProvider, setSmtpProvider] = useState<SmtpProvider>('SendGrid')
  const [smtpConnected, setSmtpConnected] = useState(false)
  const [smtpSaving, setSmtpSaving] = useState(false)

  // ─── WhatsApp state ───────────────────────────────────────
  const [waPhoneNumber, setWaPhoneNumber] = useState('')
  const [waAccessToken, setWaAccessToken] = useState('')
  const [waBusinessId, setWaBusinessId] = useState('')
  const [waConnected, setWaConnected] = useState(false)
  const [waSaving, setWaSaving] = useState(false)
  const [whatsappContacts, setWhatsappContacts] = useState<WhatsAppContact[]>(initialWhatsappContacts)
  const [isWaMessageOpen, setIsWaMessageOpen] = useState(false)
  const [waMessageText, setWaMessageText] = useState('')

  // ─── Telegram state ───────────────────────────────────────
  const [tgBotToken, setTgBotToken] = useState('')
  const [tgChatId, setTgChatId] = useState('')
  const [tgConnected, setTgConnected] = useState(false)
  const [tgSaving, setTgSaving] = useState(false)
  const [tgBroadcastText, setTgBroadcastText] = useState('')
  const [tgBroadcastHistory, setTgBroadcastHistory] = useState<{ text: string; sentAt: string }[]>([])

  // ─── Social state ─────────────────────────────────────────
  const [socialPlatforms, setSocialPlatforms] = useState<SocialPlatform[]>(initialSocialPlatforms)
  const [isPostSchedulerOpen, setIsPostSchedulerOpen] = useState(false)
  const [postContent, setPostContent] = useState('')
  const [postDate, setPostDate] = useState('')
  const [postTime, setPostTime] = useState('')
  const [postTargetPlatforms, setPostTargetPlatforms] = useState<number[]>([])

  // ─── AI state ─────────────────────────────────────────────
  const [aiLoading, setAiLoading] = useState(false)
  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [aiModalType, setAiModalType] = useState<'subjects' | 'content' | 'analysis' | 'whatsapp' | 'telegram' | 'social'>('subjects')
  const [aiResult, setAiResult] = useState('')
  const [aiSubjectLines, setAiSubjectLines] = useState<string[]>([])

  // ─── AI Generation ────────────────────────────────────────
  const handleAIGenerate = async (type: typeof aiModalType) => {
    setAiModalType(type)
    setAiModalOpen(true)
    setAiLoading(true)
    setAiResult('')
    setAiSubjectLines([])
    try {
      let context = ''
      if (type === 'whatsapp') context = 'Channel: WhatsApp Business. Audience: 3,200 contacts. Goal: increase engagement and drive product demos. Tone: friendly and concise.'
      else if (type === 'telegram') context = 'Channel: Telegram broadcast. Audience: 1,800 subscribers. Goal: announce new feature launch. Tone: exciting and direct.'
      else if (type === 'social') context = 'Channels: Facebook, Instagram, Twitter/X, LinkedIn. Audience: 40,200 combined followers. Goal: brand awareness campaign. Tone: professional yet engaging.'
      else context = 'Campaign: Summer Sale 2026, Target: 12,847 contacts, Channels: Email (5,231 sent, 42% open rate), WhatsApp (2,156 sent, 68% response rate), Social (4.8% engagement). Industry: SaaS/Enterprise Software.'

      const result = await generateMarketingContent(type === 'whatsapp' || type === 'telegram' || type === 'social' ? 'content' : type, context)
      if (type === 'subjects' && result.subjectLines) {
        setAiSubjectLines(result.subjectLines)
      } else if (result.content) {
        setAiResult(result.content)
      } else if (result.analysis) {
        setAiResult(result.analysis)
      }
    } catch {
      if (type === 'subjects') {
        setAiSubjectLines([
          "Don't miss out — exclusive offer inside",
          'Your business deserves better tools',
          'Quick question about your growth goals',
          'New AI features just launched',
          'See how others are getting 3x results',
        ])
      } else if (type === 'whatsapp') {
        setAiResult('Hi there! 👋 We just launched our new AI-powered analytics dashboard — it helps you understand your customers in real time. Book a free demo this week and get 20% off your first quarter. Reply YES to get started!')
      } else if (type === 'telegram') {
        setAiResult('🚀 Exciting news! Our latest feature — Smart Campaign Autopilot — is now live. It uses AI to optimize send times, subject lines, and content for each of your contacts. Try it free for 14 days. Link in bio!')
      } else if (type === 'social') {
        setAiResult('📣 "Working smarter starts with the right tools." We\'re thrilled to announce our new AI Marketing Hub — manage email, WhatsApp, Telegram, and social media from one dashboard. Start your free trial today!\n\n#MarketingAutomation #AI #SaaS #GrowthHacking')
      } else {
        setAiResult('Your email open rate of 42% is well above the industry average of 21%. WhatsApp engagement is exceptional at 68%. Consider increasing social media spend to capitalize on your 4.8% engagement rate, which outperforms benchmarks.')
      }
    } finally {
      setAiLoading(false)
    }
  }

  // ─── Handlers ─────────────────────────────────────────────
  const handleSaveSmtp = async () => {
    setSmtpSaving(true)
    await new Promise((r) => setTimeout(r, 1200))
    setSmtpSaving(false)
    setSmtpConnected(true)
    showToast(`${smtpProvider} SMTP configuration saved successfully`)
  }

  const handleSaveWhatsApp = async () => {
    setWaSaving(true)
    await new Promise((r) => setTimeout(r, 1200))
    setWaSaving(false)
    setWaConnected(true)
    showToast('WhatsApp Business API connected successfully')
  }

  const handleSaveTelegram = async () => {
    setTgSaving(true)
    await new Promise((r) => setTimeout(r, 1200))
    setTgSaving(false)
    setTgConnected(true)
    showToast('Telegram bot connected successfully')
  }

  const handleToggleSocialPlatform = (id: number) => {
    setSocialPlatforms((prev) =>
      prev.map((p) =>
        p.id === id
          ? { ...p, status: p.status === 'connected' ? 'disconnected' : 'connected' }
          : p
      )
    )
    const platform = socialPlatforms.find((p) => p.id === id)
    if (platform) {
      showToast(`${platform.name} ${platform.status === 'connected' ? 'disconnected' : 'connected'}`)
    }
  }

  const handleCreateCampaign = () => {
    if (!newCampaign.name.trim()) return
    const campaign: EmailCampaign = {
      id: Date.now(),
      name: newCampaign.name,
      status: 'draft',
      sent: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      scheduledFor: null,
    }
    setCampaigns((prev) => [campaign, ...prev])
    setNewCampaign({ name: '', subject: '', body: '' })
    setIsCreateCampaignOpen(false)
    showToast('Campaign created successfully')
  }

  const handleScheduleCampaign = () => {
    if (selectedCampaignId && scheduleDate && scheduleTime) {
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === selectedCampaignId
            ? { ...c, scheduledFor: `${scheduleDate}T${scheduleTime}`, status: 'active' }
            : c
        )
      )
      setIsScheduleModalOpen(false)
      setScheduleDate('')
      setScheduleTime('')
      showToast('Campaign scheduled successfully')
    }
  }

  const handleToggleCampaignStatus = (id: number) => {
    setCampaigns((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, status: c.status === 'active' ? 'paused' : c.status === 'paused' ? 'active' : c.status }
          : c
      )
    )
  }

  const handleSendWaMessage = () => {
    if (!waMessageText.trim()) return
    setIsWaMessageOpen(false)
    setWaMessageText('')
    showToast('WhatsApp message queued for delivery')
  }

  const handleSendTgBroadcast = () => {
    if (!tgBroadcastText.trim()) return
    setTgBroadcastHistory((prev) => [
      { text: tgBroadcastText, sentAt: new Date().toLocaleString() },
      ...prev,
    ])
    setTgBroadcastText('')
    showToast('Telegram broadcast sent successfully')
  }

  const handleSchedulePost = () => {
    if (!postContent.trim() || postTargetPlatforms.length === 0) return
    setSocialPlatforms((prev) =>
      prev.map((p) =>
        postTargetPlatforms.includes(p.id) ? { ...p, postsScheduled: p.postsScheduled + 1 } : p
      )
    )
    setIsPostSchedulerOpen(false)
    setPostContent('')
    setPostDate('')
    setPostTime('')
    setPostTargetPlatforms([])
    showToast('Post scheduled successfully')
  }

  const handleInsertSubjectLine = (line: string) => {
    setNewCampaign((prev) => ({ ...prev, subject: line }))
    setAiModalOpen(false)
    showToast('Subject line inserted')
  }

  const messageStatusColor: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' }> = {
    delivered: { variant: 'success' },
    read: { variant: 'info' },
    pending: { variant: 'warning' },
    failed: { variant: 'error' },
  }

  const campaignStatusColor: Record<string, 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
    active: 'success',
    draft: 'warning',
    paused: 'error',
    completed: 'info',
  }

  // Filtered campaigns
  const filteredCampaigns = campaigns.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      {/* Toast */}
      <AnimatePresence>
        {toast.visible && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className="fixed top-6 left-1/2 z-[100] bg-success-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium"
          >
            <CheckCircleIcon className="w-5 h-5" />
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Header ───────────────────────────────────────── */}
      <motion.div variants={item} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-metallic-900">Marketing Hub</h1>
          <p className="text-metallic-500 mt-1">
            Manage multi-channel campaigns with AI-powered content generation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<Cog6ToothIcon className="w-5 h-5" />}
            onClick={() => {
              if (activeTab === 'email') {
                showToast('Settings opened — configure in the connection panel below')
              } else {
                showToast('Provider settings available in the connection panel')
              }
            }}
          >
            Settings
          </Button>
          <Button
            leftIcon={<PlusIcon className="w-5 h-5" />}
            onClick={() => {
              if (activeTab === 'email') setIsCreateCampaignOpen(true)
              else if (activeTab === 'whatsapp') setIsWaMessageOpen(true)
              else if (activeTab === 'telegram') setTgBroadcastText(' ')
              else setIsPostSchedulerOpen(true)
            }}
          >
            {activeTab === 'email' && 'Create Campaign'}
            {activeTab === 'whatsapp' && 'New Message'}
            {activeTab === 'telegram' && 'Broadcast'}
            {activeTab === 'social' && 'Schedule Post'}
          </Button>
        </div>
      </motion.div>

      {/* ── Stats Cards ──────────────────────────────────── */}
      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary-500 to-primary-600 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-5 h-5" />
            </div>
            <p className="text-primary-100 text-sm font-medium">Total Contacts</p>
          </div>
          <p className="text-3xl font-bold mt-1">12,847</p>
          <p className="text-primary-200 text-sm mt-1">+234 this week</p>
        </Card>
        <Card>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
              <PaperAirplaneIcon className="w-5 h-5 text-primary-600" />
            </div>
            <p className="text-metallic-500 text-sm font-medium">Messages Sent</p>
          </div>
          <p className="text-3xl font-bold text-metallic-900 mt-1">23,841</p>
          <p className="text-success-500 text-sm mt-1">+18% vs last month</p>
        </Card>
        <Card>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-success-50 rounded-xl flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-success-600" />
            </div>
            <p className="text-metallic-500 text-sm font-medium">Engagement Rate</p>
          </div>
          <p className="text-3xl font-bold text-metallic-900 mt-1">4.8%</p>
          <p className="text-success-500 text-sm mt-1">Above 2.1% avg</p>
        </Card>
        <Card>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-warning-50 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-5 h-5 text-warning-600" />
            </div>
            <p className="text-metallic-500 text-sm font-medium">Campaign ROI</p>
          </div>
          <p className="text-3xl font-bold text-metallic-900 mt-1">324%</p>
          <p className="text-success-500 text-sm mt-1">+$12.4k revenue</p>
        </Card>
      </motion.div>

      {/* ── Channel Tabs ─────────────────────────────────── */}
      <motion.div variants={item}>
        <div className="flex gap-2 border-b border-metallic-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-metallic-500 hover:text-metallic-700'
              )}
            >
              <tab.icon className="w-5 h-5" />
              {tab.name}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════════════════
          EMAIL TAB
         ═══════════════════════════════════════════════════════ */}
      {activeTab === 'email' && (
        <motion.div variants={item} className="space-y-6">
          {/* SMTP Connection Panel */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
                  <Cog6ToothIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-metallic-900">Email Provider Connection</h3>
                  <p className="text-sm text-metallic-500">Configure your SMTP settings to send emails</p>
                </div>
              </div>
              {smtpConnected && (
                <Badge variant="success" dot>
                  <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-metallic-700 mb-2">Provider</label>
                <select
                  value={smtpProvider}
                  onChange={(e) => setSmtpProvider(e.target.value as SmtpProvider)}
                  className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                >
                  {smtpProviders.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <Input
                label="SMTP Host"
                placeholder={smtpProvider === 'Amazon SES' ? 'email-smtp.us-east-1.amazonaws.com' : smtpProvider === 'SendGrid' ? 'smtp.sendgrid.net' : smtpProvider === 'Mailgun' ? 'smtp.mailgun.org' : 'smtp.example.com'}
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                leftIcon={<LinkIcon className="w-5 h-5" />}
              />
              <Input
                label="Port"
                placeholder="587"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                leftIcon={<Cog6ToothIcon className="w-5 h-5" />}
              />
              <Input
                label="Username"
                placeholder="your@email.com"
                value={smtpUsername}
                onChange={(e) => setSmtpUsername(e.target.value)}
                leftIcon={<EnvelopeIcon className="w-5 h-5" />}
              />
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
              />
              <div className="flex items-end">
                <Button
                  leftIcon={smtpSaving ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : <LinkIcon className="w-5 h-5" />}
                  onClick={handleSaveSmtp}
                  isLoading={smtpSaving}
                  className="w-full"
                >
                  {smtpConnected ? 'Update Connection' : 'Connect Provider'}
                </Button>
              </div>
            </div>
          </Card>

          {/* Campaign List */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-metallic-900">Email Campaigns</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                  className="w-64"
                />
                <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('subjects')}>
                  <SparklesIcon className="w-4 h-4 mr-1" />
                  AI Subjects
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('content')}>
                  <SparklesIcon className="w-4 h-4 mr-1" />
                  AI Content
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('analysis')}>
                  <SparklesIcon className="w-4 h-4 mr-1" />
                  AI Analysis
                </Button>
                <Button variant="secondary" size="sm">
                  <FunnelIcon className="w-4 h-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-metallic-500 border-b border-metallic-100">
                    <th className="pb-3 font-medium">Campaign</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium text-right">Sent</th>
                    <th className="pb-3 font-medium text-right">Opened</th>
                    <th className="pb-3 font-medium text-right">Clicked</th>
                    <th className="pb-3 font-medium text-right">Bounced</th>
                    <th className="pb-3 font-medium text-right">Open Rate</th>
                    <th className="pb-3 font-medium">Schedule</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-metallic-100">
                  {filteredCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-metallic-50 transition-colors">
                      <td className="py-4 font-medium text-metallic-900">{campaign.name}</td>
                      <td className="py-4">
                        <Badge variant={campaignStatusColor[campaign.status]} dot>
                          {campaign.status}
                        </Badge>
                      </td>
                      <td className="py-4 text-metallic-600 text-right">{campaign.sent.toLocaleString()}</td>
                      <td className="py-4 text-metallic-600 text-right">{campaign.opened.toLocaleString()}</td>
                      <td className="py-4 text-metallic-600 text-right">{campaign.clicked.toLocaleString()}</td>
                      <td className="py-4 text-right">
                        <span className={clsx('font-medium', campaign.bounced > 30 ? 'text-error-500' : 'text-metallic-600')}>
                          {campaign.bounced}
                        </span>
                      </td>
                      <td className="py-4 text-metallic-600 text-right">
                        {campaign.sent > 0 ? `${((campaign.opened / campaign.sent) * 100).toFixed(1)}%` : '-'}
                      </td>
                      <td className="py-4">
                        {campaign.scheduledFor ? (
                          <div className="flex items-center gap-1.5 text-sm text-primary-600">
                            <CalendarDaysIcon className="w-4 h-4" />
                            {new Date(campaign.scheduledFor).toLocaleDateString()}{' '}
                            {new Date(campaign.scheduledFor).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        ) : (
                          <span className="text-metallic-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="py-4">
                        <div className="flex items-center justify-end gap-1">
                          {(campaign.status === 'active' || campaign.status === 'paused') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleCampaignStatus(campaign.id)}
                            >
                              {campaign.status === 'active' ? (
                                <PauseIcon className="w-4 h-4" />
                              ) : (
                                <PlayIcon className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCampaignId(campaign.id)
                              setIsScheduleModalOpen(true)
                            }}
                          >
                            <CalendarDaysIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>
      )}

      {/* ═══════════════════════════════════════════════════════
          WHATSAPP TAB
         ═══════════════════════════════════════════════════════ */}
      {activeTab === 'whatsapp' && (
        <motion.div variants={item} className="space-y-6">
          {/* WhatsApp Business API Connection */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-success-50 rounded-xl flex items-center justify-center">
                  <ChatBubbleLeftIcon className="w-5 h-5 text-success-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-metallic-900">WhatsApp Business API</h3>
                  <p className="text-sm text-metallic-500">Connect your WhatsApp Business account</p>
                </div>
              </div>
              {waConnected && (
                <Badge variant="success" dot>
                  <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Phone Number ID"
                placeholder="e.g., 1234567890"
                value={waPhoneNumber}
                onChange={(e) => setWaPhoneNumber(e.target.value)}
                leftIcon={<DevicePhoneMobileIcon className="w-5 h-5" />}
              />
              <Input
                label="Access Token"
                type="password"
                placeholder="••••••••••••••••"
                value={waAccessToken}
                onChange={(e) => setWaAccessToken(e.target.value)}
              />
              <Input
                label="Business Account ID"
                placeholder="e.g., 0987654321"
                value={waBusinessId}
                onChange={(e) => setWaBusinessId(e.target.value)}
                leftIcon={<Cog6ToothIcon className="w-5 h-5" />}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                leftIcon={waSaving ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : <LinkIcon className="w-5 h-5" />}
                onClick={handleSaveWhatsApp}
                isLoading={waSaving}
              >
                {waConnected ? 'Update Connection' : 'Connect WhatsApp'}
              </Button>
            </div>
          </Card>

          {/* AI Generation for WhatsApp */}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('whatsapp')}>
              <SparklesIcon className="w-4 h-4 mr-1" />
              AI Generate Message
            </Button>
          </div>

          {/* Quick Message Templates */}
          <Card>
            <h3 className="text-lg font-semibold text-metallic-900 mb-4">Quick Message Templates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {messageTemplates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 rounded-xl border border-metallic-200 hover:border-primary-300 hover:bg-primary-50 transition-all cursor-pointer"
                  onClick={() => {
                    setWaMessageText(template.body)
                    setIsWaMessageOpen(true)
                  }}
                >
                  <p className="font-medium text-metallic-900 text-sm">{template.name}</p>
                  <p className="text-sm text-metallic-500 mt-1 line-clamp-2">{template.body}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Contact List */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-metallic-900">Contacts</h3>
              <Button leftIcon={<PlusIcon className="w-4 h-4" />} size="sm">
                Add Contact
              </Button>
            </div>
            <div className="space-y-3">
              {whatsappContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-metallic-50 transition-colors"
                >
                  <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center">
                    <ChatBubbleLeftIcon className="w-6 h-6 text-success-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-metallic-900">{contact.name}</p>
                    <p className="text-sm text-metallic-500">{contact.phone}</p>
                    <p className="text-sm text-metallic-400 mt-0.5 truncate">{contact.lastMessage}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={contact.status === 'active' ? 'success' : 'info'} dot>
                      {contact.status}
                    </Badge>
                    <Badge variant={messageStatusColor[contact.messageStatus]?.variant || 'info'} size="sm">
                      {contact.messageStatus}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      )}

      {/* ═══════════════════════════════════════════════════════
          TELEGRAM TAB
         ═══════════════════════════════════════════════════════ */}
      {activeTab === 'telegram' && (
        <motion.div variants={item} className="space-y-6">
          {/* Telegram Bot Connection */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-info-100 rounded-xl flex items-center justify-center">
                  <PaperAirplaneIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-metallic-900">Telegram Bot Connection</h3>
                  <p className="text-sm text-metallic-500">Connect your Telegram bot for broadcasts</p>
                </div>
              </div>
              {tgConnected && (
                <Badge variant="success" dot>
                  <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Bot Token"
                type="password"
                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                value={tgBotToken}
                onChange={(e) => setTgBotToken(e.target.value)}
                leftIcon={<Cog6ToothIcon className="w-5 h-5" />}
              />
              <Input
                label="Chat ID"
                placeholder="-1001234567890"
                value={tgChatId}
                onChange={(e) => setTgChatId(e.target.value)}
                leftIcon={<ChatBubbleLeftIcon className="w-5 h-5" />}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                leftIcon={tgSaving ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : <LinkIcon className="w-5 h-5" />}
                onClick={handleSaveTelegram}
                isLoading={tgSaving}
              >
                {tgConnected ? 'Update Connection' : 'Connect Bot'}
              </Button>
            </div>
          </Card>

          {/* AI Content Suggestion */}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('telegram')}>
              <SparklesIcon className="w-4 h-4 mr-1" />
              AI Content Suggestion
            </Button>
          </div>

          {/* Message Broadcast */}
          <Card>
            <h3 className="text-lg font-semibold text-metallic-900 mb-4">Message Broadcast</h3>
            <div className="space-y-4">
              <textarea
                value={tgBroadcastText}
                onChange={(e) => setTgBroadcastText(e.target.value)}
                placeholder="Type your broadcast message here..."
                className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 placeholder-metallic-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none h-32"
              />
              <div className="flex items-center justify-between">
                <p className="text-sm text-metallic-500">
                  {tgBroadcastText.length} / 4096 characters
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => handleAIGenerate('telegram')}>
                    <SparklesIcon className="w-4 h-4 mr-1" />
                    AI Suggest
                  </Button>
                  <Button
                    leftIcon={<PaperAirplaneIcon className="w-5 h-5" />}
                    onClick={handleSendTgBroadcast}
                    disabled={!tgBroadcastText.trim()}
                  >
                    Send Broadcast
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Broadcast History */}
          {tgBroadcastHistory.length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-metallic-900 mb-4">Broadcast History</h3>
              <div className="space-y-3">
                {tgBroadcastHistory.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-metallic-50 rounded-xl">
                    <PaperAirplaneIcon className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-metallic-700">{entry.text}</p>
                      <p className="text-xs text-metallic-400 mt-1">{entry.sentAt}</p>
                    </div>
                    <Badge variant="success" size="sm">Sent</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </motion.div>
      )}

      {/* ═══════════════════════════════════════════════════════
          SOCIAL MEDIA TAB
         ═══════════════════════════════════════════════════════ */}
      {activeTab === 'social' && (
        <motion.div variants={item} className="space-y-6">
          {/* AI Post Generation */}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('social')}>
              <SparklesIcon className="w-4 h-4 mr-1" />
              AI Generate Post
            </Button>
            <Button
              leftIcon={<CalendarDaysIcon className="w-5 h-5" />}
              size="sm"
              onClick={() => setIsPostSchedulerOpen(true)}
            >
              Schedule Post
            </Button>
          </div>

          {/* Platform Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {socialPlatforms.map((platform) => (
              <Card key={platform.id} hover>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{platform.icon}</span>
                    <div>
                      <h4 className="font-semibold text-metallic-900">{platform.name}</h4>
                      <p className="text-xs text-metallic-500">
                        {platform.postsScheduled} posts scheduled
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={platform.status === 'connected' ? 'success' : platform.status === 'pending' ? 'warning' : 'info'}
                    dot
                  >
                    {platform.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-metallic-500">Followers</p>
                    <p className="font-semibold text-metallic-900">{platform.followers.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-metallic-500">Engagement</p>
                    <p className="font-semibold text-metallic-900">{platform.engagement}%</p>
                  </div>
                </div>
                <Button
                  variant={platform.status === 'connected' ? 'danger' : 'primary'}
                  size="sm"
                  className="w-full"
                  leftIcon={
                    platform.status === 'connected' ? (
                      <LinkIcon className="w-4 h-4" />
                    ) : (
                      <CheckCircleIcon className="w-4 h-4" />
                    )
                  }
                  onClick={() => handleToggleSocialPlatform(platform.id)}
                >
                  {platform.status === 'connected' ? 'Disconnect' : 'Connect via OAuth'}
                </Button>
              </Card>
            ))}
          </div>

          {/* Platform Summary */}
          <Card>
            <h3 className="text-lg font-semibold text-metallic-900 mb-4">Cross-Platform Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-metallic-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-metallic-900">
                  {socialPlatforms.reduce((sum, p) => sum + p.followers, 0).toLocaleString()}
                </p>
                <p className="text-sm text-metallic-500 mt-1">Total Followers</p>
              </div>
              <div className="p-4 bg-metallic-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-metallic-900">
                  {(socialPlatforms.reduce((sum, p) => sum + p.engagement, 0) / socialPlatforms.length).toFixed(1)}%
                </p>
                <p className="text-sm text-metallic-500 mt-1">Avg Engagement</p>
              </div>
              <div className="p-4 bg-metallic-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-metallic-900">
                  {socialPlatforms.filter((p) => p.status === 'connected').length}
                </p>
                <p className="text-sm text-metallic-500 mt-1">Connected</p>
              </div>
              <div className="p-4 bg-metallic-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-metallic-900">
                  {socialPlatforms.reduce((sum, p) => sum + p.postsScheduled, 0)}
                </p>
                <p className="text-sm text-metallic-500 mt-1">Posts Scheduled</p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MODALS
         ═══════════════════════════════════════════════════════ */}

      {/* Create Campaign Modal */}
      <Modal
        isOpen={isCreateCampaignOpen}
        onClose={() => setIsCreateCampaignOpen(false)}
        title="Create Email Campaign"
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Campaign Name"
            placeholder="e.g., Summer Sale 2026"
            value={newCampaign.name}
            onChange={(e) => setNewCampaign((prev) => ({ ...prev, name: e.target.value }))}
          />
          <div className="relative">
            <Input
              label="Subject Line"
              placeholder="Enter email subject or generate with AI"
              value={newCampaign.subject}
              onChange={(e) => setNewCampaign((prev) => ({ ...prev, subject: e.target.value }))}
              rightIcon={
                <button
                  onClick={() => handleAIGenerate('subjects')}
                  className="text-primary-500 hover:text-primary-700"
                >
                  <SparklesIcon className="w-5 h-5" />
                </button>
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-metallic-700 mb-2">Email Body</label>
            <div className="relative">
              <textarea
                value={newCampaign.body}
                onChange={(e) => setNewCampaign((prev) => ({ ...prev, body: e.target.value }))}
                placeholder="Write your email content or use AI to generate it..."
                className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 placeholder-metallic-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none h-40"
              />
              <button
                onClick={() => handleAIGenerate('content')}
                className="absolute bottom-3 right-3 text-primary-500 hover:text-primary-700 flex items-center gap-1 text-xs font-medium bg-primary-50 px-2 py-1 rounded-lg"
              >
                <SparklesIcon className="w-4 h-4" />
                AI Generate
              </button>
            </div>
          </div>

          {/* Template Builder */}
          <div>
            <label className="block text-sm font-medium text-metallic-700 mb-2">Template Builder</label>
            <div className="grid grid-cols-3 gap-2">
              {['Header', 'Text Block', 'Image', 'Button', 'Divider', 'Footer'].map((block) => (
                <button
                  key={block}
                  className="p-3 border-2 border-dashed border-metallic-200 rounded-xl text-sm font-medium text-metallic-600 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600 transition-all"
                >
                  <PlusIcon className="w-4 h-4 mx-auto mb-1" />
                  {block}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setIsCreateCampaignOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign}>Create Campaign</Button>
          </div>
        </div>
      </Modal>

      {/* Schedule Campaign Modal */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        title="Schedule Campaign"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-metallic-500">
            Schedule this campaign to be sent at a specific date and time.
          </p>
          <Input
            label="Date"
            type="date"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            leftIcon={<CalendarDaysIcon className="w-5 h-5" />}
          />
          <Input
            label="Time"
            type="time"
            value={scheduleTime}
            onChange={(e) => setScheduleTime(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setIsScheduleModalOpen(false)}>Cancel</Button>
            <Button
              leftIcon={<CalendarDaysIcon className="w-5 h-5" />}
              onClick={handleScheduleCampaign}
              disabled={!scheduleDate || !scheduleTime}
            >
              Schedule
            </Button>
          </div>
        </div>
      </Modal>

      {/* WhatsApp Message Modal */}
      <Modal
        isOpen={isWaMessageOpen}
        onClose={() => setIsWaMessageOpen(false)}
        title="Send WhatsApp Message"
        size="md"
      >
        <div className="space-y-4">
          <textarea
            value={waMessageText}
            onChange={(e) => setWaMessageText(e.target.value)}
            placeholder="Type your message..."
            className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 placeholder-metallic-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none h-32"
          />
          <div className="flex items-center justify-between">
            <p className="text-sm text-metallic-500">{waMessageText.length} / 1024 characters</p>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => handleAIGenerate('whatsapp')}>
                <SparklesIcon className="w-4 h-4 mr-1" />
                AI Suggest
              </Button>
              <Button
                leftIcon={<ChatBubbleLeftIcon className="w-5 h-5" />}
                onClick={handleSendWaMessage}
                disabled={!waMessageText.trim()}
              >
                Send Message
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Social Post Scheduler Modal */}
      <Modal
        isOpen={isPostSchedulerOpen}
        onClose={() => setIsPostSchedulerOpen(false)}
        title="Schedule Social Media Post"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-metallic-700 mb-2">Post Content</label>
            <div className="relative">
              <textarea
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                placeholder="Write your post or generate with AI..."
                className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 placeholder-metallic-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none h-32"
              />
              <button
                onClick={() => handleAIGenerate('social')}
                className="absolute bottom-3 right-3 text-primary-500 hover:text-primary-700 flex items-center gap-1 text-xs font-medium bg-primary-50 px-2 py-1 rounded-lg"
              >
                <SparklesIcon className="w-4 h-4" />
                AI Generate
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-metallic-700 mb-2">Target Platforms</label>
            <div className="flex flex-wrap gap-2">
              {socialPlatforms
                .filter((p) => p.status === 'connected')
                .map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() =>
                      setPostTargetPlatforms((prev) =>
                        prev.includes(platform.id)
                          ? prev.filter((id) => id !== platform.id)
                          : [...prev, platform.id]
                      )
                    }
                    className={clsx(
                      'flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all',
                      postTargetPlatforms.includes(platform.id)
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-metallic-200 text-metallic-600 hover:border-primary-300'
                    )}
                  >
                    <span>{platform.icon}</span>
                    {platform.name}
                    {postTargetPlatforms.includes(platform.id) && (
                      <CheckCircleIcon className="w-4 h-4" />
                    )}
                  </button>
                ))}
              {socialPlatforms.filter((p) => p.status === 'connected').length === 0 && (
                <p className="text-sm text-metallic-400">Connect platforms in the Social Media tab first.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Date"
              type="date"
              value={postDate}
              onChange={(e) => setPostDate(e.target.value)}
              leftIcon={<CalendarDaysIcon className="w-5 h-5" />}
            />
            <Input
              label="Time"
              type="time"
              value={postTime}
              onChange={(e) => setPostTime(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setIsPostSchedulerOpen(false)}>Cancel</Button>
            <Button
              leftIcon={<CalendarDaysIcon className="w-5 h-5" />}
              onClick={handleSchedulePost}
              disabled={!postContent.trim() || postTargetPlatforms.length === 0 || !postDate || !postTime}
            >
              Schedule Post
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI Content Modal */}
      <Modal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        title={
          aiModalType === 'subjects'
            ? 'AI Subject Line Generator'
            : aiModalType === 'content'
              ? 'AI Content Generator'
              : aiModalType === 'analysis'
                ? 'AI Campaign Analysis'
                : aiModalType === 'whatsapp'
                  ? 'AI WhatsApp Message'
                  : aiModalType === 'telegram'
                    ? 'AI Telegram Content'
                    : 'AI Social Media Post'
        }
        size="lg"
      >
        {aiLoading ? (
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="relative">
              <SparklesIcon className="w-10 h-10 text-primary-500 animate-pulse" />
              <div className="absolute inset-0 w-10 h-10 bg-primary-200 rounded-full animate-ping opacity-20" />
            </div>
            <p className="text-metallic-500 text-sm font-medium">Generating content with Groq AI...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {aiModalType === 'subjects' && (
              <>
                <h4 className="font-semibold text-metallic-900">Generated Subject Lines</h4>
                <p className="text-sm text-metallic-500">Click a subject line to insert it into your campaign.</p>
                <div className="space-y-2">
                  {aiSubjectLines.map((line, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 bg-metallic-50 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer"
                      onClick={() => handleInsertSubjectLine(line)}
                    >
                      <Badge variant="info" size="sm">{i + 1}</Badge>
                      <p className="text-sm text-metallic-700 flex-1">{line}</p>
                      <span className="text-xs text-metallic-400">Click to use</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {aiModalType === 'content' && (
              <>
                <h4 className="font-semibold text-metallic-900">Generated Marketing Content</h4>
                <div className="p-4 bg-metallic-50 rounded-xl text-sm text-metallic-700 whitespace-pre-line leading-relaxed">
                  {aiResult}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setNewCampaign((prev) => ({ ...prev, body: aiResult }))
                    setAiModalOpen(false)
                    showToast('Content inserted into campaign body')
                  }}
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Insert into Campaign
                </Button>
              </>
            )}
            {aiModalType === 'analysis' && (
              <>
                <h4 className="font-semibold text-metallic-900">Campaign Analysis</h4>
                <div className="p-4 bg-primary-50 rounded-xl text-sm text-metallic-700 leading-relaxed">
                  {aiResult}
                </div>
              </>
            )}
            {(aiModalType === 'whatsapp' || aiModalType === 'telegram' || aiModalType === 'social') && (
              <>
                <h4 className="font-semibold text-metallic-900">AI Generated Content</h4>
                <div className="p-4 bg-metallic-50 rounded-xl text-sm text-metallic-700 whitespace-pre-line leading-relaxed">
                  {aiResult}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (aiModalType === 'whatsapp') {
                      setWaMessageText(aiResult)
                      setIsWaMessageOpen(true)
                    } else if (aiModalType === 'telegram') {
                      setTgBroadcastText(aiResult)
                    } else {
                      setPostContent(aiResult)
                      setIsPostSchedulerOpen(true)
                    }
                    setAiModalOpen(false)
                    showToast('Content inserted')
                  }}
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Use This Content
                </Button>
              </>
            )}
            <div className="flex justify-end gap-3 pt-2 border-t border-metallic-100">
              <Button variant="ghost" onClick={() => setAiModalOpen(false)}>Close</Button>
              <Button variant="secondary" onClick={() => handleAIGenerate(aiModalType)}>
                <SparklesIcon className="w-4 h-4 mr-1" />
                Regenerate
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  )
}
